<template>
  <div class="salesforce-monitoring-dashboard">
    <!-- Header -->
    <div class="bg-white shadow-sm border-b">
      <div class="px-6 py-4">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900">Salesforce Sync Monitoring</h1>
            <p class="text-sm text-gray-600 mt-1">Real-time sync status and performance metrics</p>
          </div>
          <div class="flex space-x-3">
            <select v-model="timeRange" @change="refreshData" class="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
            <button @click="refreshData" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
              <svg class="w-4 h-4 inline mr-2" :class="{ 'animate-spin': isLoading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Health Status Bar -->
    <div class="bg-white shadow-sm border-b px-6 py-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <span class="text-sm font-medium text-gray-700">System Health:</span>
          <div class="flex items-center space-x-2">
            <span :class="healthStatusClass" class="w-3 h-3 rounded-full"></span>
            <span class="text-sm font-medium capitalize">{{ healthStatus.status }}</span>
          </div>
        </div>
        <div class="flex items-center space-x-6 text-sm">
          <div v-for="check in healthStatus.checks" :key="check.name" class="flex items-center space-x-2">
            <span :class="check.healthy ? 'bg-green-500' : 'bg-red-500'" class="w-2 h-2 rounded-full"></span>
            <span class="text-gray-600">{{ check.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Grid -->
    <div class="p-6">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Total Syncs</p>
              <p class="text-2xl font-semibold text-gray-900">{{ stats.total_syncs }}</p>
            </div>
            <div class="bg-blue-100 rounded-full p-3">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Success Rate</p>
              <p class="text-2xl font-semibold text-green-600">{{ stats.sync_success_rate }}%</p>
            </div>
            <div class="bg-green-100 rounded-full p-3">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Failed Syncs</p>
              <p class="text-2xl font-semibold" :class="stats.failed_syncs > 0 ? 'text-red-600' : 'text-gray-900'">
                {{ stats.failed_syncs }}
              </p>
            </div>
            <div :class="stats.failed_syncs > 0 ? 'bg-red-100' : 'bg-gray-100'" class="rounded-full p-3">
              <svg class="w-6 h-6" :class="stats.failed_syncs > 0 ? 'text-red-600' : 'text-gray-600'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Pending Queue</p>
              <p class="text-2xl font-semibold text-gray-900">{{ stats.pending_syncs }}</p>
            </div>
            <div class="bg-yellow-100 rounded-full p-3">
              <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="bg-white rounded-lg shadow">
        <div class="border-b border-gray-200">
          <nav class="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
              ]"
            >
              {{ tab.name }}
              <span v-if="tab.count !== undefined" class="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                {{ tab.count }}
              </span>
            </button>
          </nav>
        </div>

        <!-- Tab Content -->
        <div class="p-6">
          <!-- Recent Logs Tab -->
          <div v-if="activeTab === 'logs'" class="space-y-4">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium text-gray-900">Sync Logs</h3>
              <div class="flex space-x-2">
                <select v-model="logFilter.status" @change="loadLogs" class="text-sm rounded-md border-gray-300">
                  <option value="">All Status</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="logsLoading" class="text-center py-8">
              <svg class="animate-spin h-8 w-8 mx-auto text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="mt-2 text-sm text-gray-600">Loading logs...</p>
            </div>

            <div v-else class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salesforce ID</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="log in logs" :key="log.id">
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ log.campaign?.name || 'Campaign #' + (log.entity_id || log.campaign_id) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="getStatusClass(log.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                        {{ log.status }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <a v-if="log.salesforce_id || log.sf_record_id" 
                         :href="getSalesforceUrl(log.salesforce_id || log.sf_record_id, log.sf_record_type)"
                         target="_blank"
                         class="text-blue-600 hover:text-blue-900 hover:underline">
                        {{ log.salesforce_id || log.sf_record_id }}
                      </a>
                      <span v-else>-</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ formatDate(log.created_at) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button @click="viewLogDetail(log)" class="text-blue-600 hover:text-blue-900 mr-3">View</button>
                      <button v-if="log.status === 'failed'" @click="retrySync(log)" class="text-green-600 hover:text-green-900 mr-3">Retry</button>
                      <button v-if="log.status === 'success'" @click="retrySync(log)" class="text-green-600 hover:text-green-900 mr-3">Resend</button>
                      <button @click="deleteLog(log)" class="text-red-600 hover:text-red-900">Delete</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="logsPagination.total > logsPagination.per_page" class="flex items-center justify-between">
              <div class="text-sm text-gray-700">
                Showing {{ logsPagination.from }} to {{ logsPagination.to }} of {{ logsPagination.total }} results
              </div>
              <div class="flex space-x-2">
                <button 
                  @click="loadLogs(logsPagination.current_page - 1)" 
                  :disabled="logsPagination.current_page === 1"
                  class="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <button 
                  @click="loadLogs(logsPagination.current_page + 1)" 
                  :disabled="logsPagination.current_page === logsPagination.last_page"
                  class="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <!-- Queue Tab -->
          <div v-if="activeTab === 'queue'" class="space-y-4">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium text-gray-900">Sync Queue</h3>
              <button @click="loadQueue(1)" class="text-sm text-blue-600 hover:text-blue-900">
                Refresh Queue
              </button>
            </div>

            <!-- Loading State -->
            <div v-if="queueLoading" class="text-center py-8">
              <svg class="animate-spin h-8 w-8 mx-auto text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="mt-2 text-sm text-gray-600">Loading queue...</p>
            </div>

            <div v-else class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attempts</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Retry</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="item in queueItems" :key="item.id">
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ item.campaign?.name || 'Campaign #' + item.campaign_id }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="getStatusClass(item.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                        {{ item.status }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ item.attempts }} / {{ item.max_attempts }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ item.next_retry_at ? formatDate(item.next_retry_at) : '-' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button @click="retryQueueItem(item)" class="text-green-600 hover:text-green-900">Retry Now</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Pagination -->
            <div v-if="!queueLoading && queuePagination.total > queuePagination.per_page" class="flex items-center justify-between mt-4">
              <div class="text-sm text-gray-700">
                Showing {{ queuePagination.from }} to {{ queuePagination.to }} of {{ queuePagination.total }} results
              </div>
              <div class="flex space-x-2">
                <button 
                  @click="loadQueue(queuePagination.current_page - 1)" 
                  :disabled="queuePagination.current_page === 1"
                  class="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <button 
                  @click="loadQueue(queuePagination.current_page + 1)" 
                  :disabled="queuePagination.current_page === queuePagination.last_page"
                  class="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <!-- Pending Campaigns Tab -->
          <div v-if="activeTab === 'pending'" class="space-y-4">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium text-gray-900">Campaigns Pending Sync</h3>
              <button @click="showBulkSyncModal = true" :disabled="selectedCampaigns.length === 0" 
                      class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                Bulk Sync ({{ selectedCampaigns.length }})
              </button>
            </div>

            <!-- Loading State -->
            <div v-if="pendingLoading" class="text-center py-8">
              <svg class="animate-spin h-8 w-8 mx-auto text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="mt-2 text-sm text-gray-600">Loading pending campaigns...</p>
            </div>

            <div v-else class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input type="checkbox" @change="toggleAllCampaigns" v-model="selectAll" class="rounded border-gray-300">
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Foundation</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="campaign in pendingCampaigns" :key="campaign.id">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <input type="checkbox" v-model="selectedCampaigns" :value="campaign.id" class="rounded border-gray-300">
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ campaign.name }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ campaign.parent?.name || '-' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ formatDate(campaign.created_at) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button @click="syncSingleCampaign(campaign)" class="text-blue-600 hover:text-blue-900">Sync Now</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Pagination -->
            <div v-if="!pendingLoading && pendingPagination.total > pendingPagination.per_page" class="flex items-center justify-between mt-4">
              <div class="text-sm text-gray-700">
                Showing {{ pendingPagination.from }} to {{ pendingPagination.to }} of {{ pendingPagination.total }} results
              </div>
              <div class="flex space-x-2">
                <button 
                  @click="loadPendingCampaigns(pendingPagination.current_page - 1)" 
                  :disabled="pendingPagination.current_page === 1"
                  class="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <button 
                  @click="loadPendingCampaigns(pendingPagination.current_page + 1)" 
                  :disabled="pendingPagination.current_page === pendingPagination.last_page"
                  class="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <!-- Errors Tab -->
          <div v-if="activeTab === 'errors'" class="space-y-4">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium text-gray-900">Recent Errors</h3>
              <button 
                v-if="stats.failed_syncs > 0"
                @click="clearAllErrors" 
                class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors">
                Clear All Errors ({{ stats.failed_syncs }})
              </button>
            </div>
            
            <div v-if="stats.recent_errors && stats.recent_errors.length > 0" class="space-y-3">
              <div v-for="error in stats.recent_errors" :key="error.id" class="bg-red-50 border border-red-200 rounded-lg p-4">
                <div class="flex justify-between items-start">
                  <div class="flex-1">
                    <p class="text-sm font-medium text-red-800">Campaign #{{ error.entity_id }}</p>
                    <p class="text-sm text-red-600 mt-1">{{ error.error_message }}</p>
                    <p class="text-xs text-gray-500 mt-2">{{ formatDate(error.created_at) }}</p>
                  </div>
                  <button @click="retryFailedSync(error.id)" class="ml-4 text-sm text-red-600 hover:text-red-900">
                    Retry
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-8 text-gray-500">
              No recent errors
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Log Detail Modal -->
    <div v-if="showLogModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-medium text-gray-900">Sync Log Details</h3>
        </div>
        <div class="px-6 py-4 overflow-y-auto max-h-[60vh]">
          <div v-if="selectedLog" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Campaign</label>
              <p class="mt-1 text-sm text-gray-900">{{ selectedLog.campaign?.name || 'Campaign #' + selectedLog.entity_id }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Status</label>
              <span :class="getStatusClass(selectedLog.status)" class="mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                {{ selectedLog.status }}
              </span>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Salesforce ID</label>
              <p class="mt-1 text-sm text-gray-900">
                <a v-if="selectedLog.salesforce_id || selectedLog.sf_record_id" 
                   :href="getSalesforceUrl(selectedLog.salesforce_id || selectedLog.sf_record_id, selectedLog.sf_record_type)"
                   target="_blank"
                   class="text-blue-600 hover:text-blue-900 hover:underline">
                  {{ selectedLog.salesforce_id || selectedLog.sf_record_id }}
                  <svg class="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                  </svg>
                </a>
                <span v-else>N/A</span>
              </p>
            </div>
            <div v-if="selectedLog.error_message">
              <label class="block text-sm font-medium text-gray-700">Error Message</label>
              <p class="mt-1 text-sm text-red-600">{{ selectedLog.error_message }}</p>
            </div>
            <div v-if="selectedLog.request_data">
              <label class="block text-sm font-medium text-gray-700">Request Data</label>
              <pre class="mt-1 text-xs bg-gray-50 p-3 rounded overflow-x-auto">{{ JSON.stringify(selectedLog.request_data, null, 2) }}</pre>
            </div>
            <div v-if="selectedLog.response_data">
              <label class="block text-sm font-medium text-gray-700">Response Data</label>
              <pre class="mt-1 text-xs bg-gray-50 p-3 rounded overflow-x-auto">{{ JSON.stringify(selectedLog.response_data, null, 2) }}</pre>
            </div>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button @click="showLogModal = false" class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk Sync Modal -->
    <div v-if="showBulkSyncModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg max-w-md w-full">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-medium text-gray-900">Bulk Sync Campaigns</h3>
        </div>
        <div class="px-6 py-4">
          <p class="text-sm text-gray-600 mb-4">
            You are about to sync {{ selectedCampaigns.length }} campaigns to Salesforce.
          </p>
          <div>
            <label class="block text-sm font-medium text-gray-700">Select Configuration</label>
            <select v-model="bulkSyncConfig" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
              <option value="">Select a configuration...</option>
              <option v-for="config in configurations" :key="config.id" :value="config.id">
                {{ config.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button @click="showBulkSyncModal = false" class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
            Cancel
          </button>
          <button @click="executeBulkSync" :disabled="!bulkSyncConfig" 
                  class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            Sync Now
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import axios from 'axios';

export default {
  name: 'SalesforceMonitoringDashboard',
  setup() {
    // State
    const isLoading = ref(false);
    const logsLoading = ref(false);
    const statsLoading = ref(false);
    const queueLoading = ref(false);
    const pendingLoading = ref(false);
    const timeRange = ref('24h');
    const activeTab = ref('logs');
    const stats = ref({
      total_syncs: 0,
      successful_syncs: 0,
      failed_syncs: 0,
      pending_syncs: 0,
      sync_success_rate: 100,
      average_sync_time: '0s',
      recent_errors: [],
      hourly_activity: []
    });
    
    const healthStatus = ref({
      status: 'healthy',
      checks: []
    });
    
    const logs = ref([]);
    const logsPagination = ref({});
    const logFilter = ref({ status: '' });
    
    const queueItems = ref([]);
    const queuePagination = ref({});
    const pendingCampaigns = ref([]);
    const pendingPagination = ref({});
    const configurations = ref([]);
    
    const selectedCampaigns = ref([]);
    const selectAll = ref(false);
    const bulkSyncConfig = ref('');
    
    const showLogModal = ref(false);
    const selectedLog = ref(null);
    const showBulkSyncModal = ref(false);
    
    let refreshInterval = null;

    // Computed
    const tabs = computed(() => [
      { id: 'logs', name: 'Recent Logs', count: stats.value.total_syncs },
      { id: 'queue', name: 'Queue', count: stats.value.pending_syncs },
      { id: 'pending', name: 'Pending Campaigns' },
      { id: 'errors', name: 'Errors', count: stats.value.failed_syncs }
    ]);

    const healthStatusClass = computed(() => {
      switch (healthStatus.value.status) {
        case 'healthy': return 'bg-green-500';
        case 'warning': return 'bg-yellow-500';
        case 'critical': return 'bg-red-500';
        default: return 'bg-gray-500';
      }
    });

    // Methods
    const refreshData = async () => {
      // Only refresh current tab data
      await loadStats();
      await loadHealthStatus();
      
      if (activeTab.value === 'logs') {
        await loadLogs(logsPagination.value.current_page || 1);
      } else if (activeTab.value === 'queue') {
        await loadQueue(queuePagination.value.current_page || 1);
      } else if (activeTab.value === 'pending') {
        await loadPendingCampaigns(pendingPagination.value.current_page || 1);
      }
    };

    const loadStats = async () => {
      statsLoading.value = true;
      try {
        const response = await axios.get('/admin/salesforce/monitoring/stats', {
          params: { range: timeRange.value }
        });
        stats.value = response.data;
      } catch (error) {
        console.error('Failed to load stats:', error);
      } finally {
        statsLoading.value = false;
      }
    };

    const loadHealthStatus = async () => {
      try {
        const response = await axios.get('/admin/salesforce/monitoring/health');
        healthStatus.value = response.data;
      } catch (error) {
        console.error('Failed to load health status:', error);
      }
    };

    const loadLogs = async (page = 1) => {
      logsLoading.value = true;
      try {
        const response = await axios.get('/admin/salesforce/monitoring/logs', {
          params: {
            page,
            per_page: 10,
            status: logFilter.value.status
          }
        });
        logs.value = response.data.data;
        logsPagination.value = response.data;
      } catch (error) {
        console.error('Failed to load logs:', error);
      } finally {
        logsLoading.value = false;
      }
    };

    const loadQueue = async (page = 1) => {
      queueLoading.value = true;
      try {
        const response = await axios.get('/admin/salesforce/monitoring/queue', {
          params: { 
            page,
            per_page: 10 
          }
        });
        queueItems.value = response.data.data || [];
        queuePagination.value = response.data;
      } catch (error) {
        console.error('Failed to load queue:', error);
      } finally {
        queueLoading.value = false;
      }
    };

    const loadPendingCampaigns = async (page = 1) => {
      pendingLoading.value = true;
      try {
        const response = await axios.get('/admin/salesforce/monitoring/pending-campaigns', {
          params: { 
            page,
            per_page: 20 
          }
        });
        pendingCampaigns.value = response.data.data || [];
        pendingPagination.value = response.data;
      } catch (error) {
        console.error('Failed to load pending campaigns:', error);
      } finally {
        pendingLoading.value = false;
      }
    };

    const loadConfigurations = async () => {
      try {
        const response = await axios.get('/admin/salesforce/configurations');
        configurations.value = response.data;
      } catch (error) {
        console.error('Failed to load configurations:', error);
      }
    };

    const viewLogDetail = async (log) => {
      try {
        const response = await axios.get(`/admin/salesforce/monitoring/logs/${log.id}`);
        selectedLog.value = response.data;
        showLogModal.value = true;
      } catch (error) {
        console.error('Failed to load log detail:', error);
      }
    };

    const retrySync = async (log) => {
      const action = log.status === 'success' ? 'resend' : 'retry';
      const confirmMessage = log.status === 'success' 
        ? 'Are you sure you want to resend this sync to Salesforce?' 
        : 'Are you sure you want to retry this failed sync?';
      
      if (!confirm(confirmMessage)) return;
      
      try {
        const response = await axios.post(`/admin/salesforce/monitoring/retry-log/${log.id}`);
        const successMessage = response.data?.message || `Sync job has been queued for ${action}`;
        alert(successMessage);
        refreshData();
      } catch (error) {
        console.error(`Failed to ${action} sync:`, error);
        alert(`Failed to ${action} sync: ` + error.response?.data?.error);
      }
    };

    const deleteLog = async (log) => {
      const message = log.status === 'failed' 
        ? 'Are you sure you want to delete this error log?' 
        : 'Are you sure you want to delete this log?';
      
      if (!confirm(message)) return;
      
      try {
        await axios.delete(`/admin/salesforce/monitoring/logs/${log.id}`);
        alert('Log deleted successfully');
        await refreshData();
      } catch (error) {
        console.error('Failed to delete log:', error);
        alert('Failed to delete log: ' + error.response?.data?.message);
      }
    };

    const clearAllErrors = async () => {
      if (!confirm(`Are you sure you want to clear all ${stats.value.failed_syncs} error logs? This action cannot be undone.`)) return;
      
      try {
        const response = await axios.delete('/admin/salesforce/monitoring/logs/clear-errors');
        alert(response.data.message);
        await refreshData();
      } catch (error) {
        console.error('Failed to clear errors:', error);
        alert('Failed to clear errors: ' + error.response?.data?.message);
      }
    };

    const retryQueueItem = async (item) => {
      if (!confirm('Are you sure you want to retry this queue item?')) return;
      
      try {
        await axios.post(`/admin/salesforce/monitoring/retry-queue/${item.id}`);
        alert('Queue item has been reset and job dispatched');
        loadQueue();
      } catch (error) {
        console.error('Failed to retry queue item:', error);
        alert('Failed to retry queue item: ' + error.response?.data?.error);
      }
    };

    const retryFailedSync = async (logId) => {
      try {
        await axios.post(`/admin/salesforce/monitoring/retry-log/${logId}`);
        alert('Sync job has been queued for retry');
        refreshData();
      } catch (error) {
        console.error('Failed to retry sync:', error);
        alert('Failed to retry sync: ' + error.response?.data?.error);
      }
    };

    const syncSingleCampaign = async (campaign) => {
      const configId = prompt('Enter configuration ID for sync:');
      if (!configId) return;
      
      try {
        await axios.post(`/admin/salesforce/sync/${campaign.id}`, {
          configuration_id: configId
        });
        alert('Campaign sync has been initiated');
        refreshData();
      } catch (error) {
        console.error('Failed to sync campaign:', error);
        alert('Failed to sync campaign: ' + error.response?.data?.message);
      }
    };

    const toggleAllCampaigns = () => {
      if (selectAll.value) {
        selectedCampaigns.value = pendingCampaigns.value.map(c => c.id);
      } else {
        selectedCampaigns.value = [];
      }
    };

    const executeBulkSync = async () => {
      if (!bulkSyncConfig.value) {
        alert('Please select a configuration');
        return;
      }
      
      try {
        const response = await axios.post('/admin/salesforce/monitoring/bulk-sync', {
          campaign_ids: selectedCampaigns.value,
          configuration_id: bulkSyncConfig.value
        });
        alert(response.data.message);
        showBulkSyncModal.value = false;
        selectedCampaigns.value = [];
        selectAll.value = false;
        refreshData();
      } catch (error) {
        console.error('Failed to execute bulk sync:', error);
        alert('Failed to execute bulk sync: ' + error.response?.data?.message);
      }
    };

    const getStatusClass = (status) => {
      switch (status) {
        case 'success': return 'bg-green-100 text-green-800';
        case 'failed': return 'bg-red-100 text-red-800';
        case 'pending': return 'bg-yellow-100 text-yellow-800';
        case 'processing': return 'bg-blue-100 text-blue-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    const formatDate = (date) => {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleString();
    };
    
    const getSalesforceUrl = (recordId, recordType) => {
      if (!recordId || !stats.value.instance_url) return '#';
      // Salesforce record URLs follow the pattern: instance_url/lightning/r/ObjectType/RecordId/view
      // Use the actual record type from the log (Lead or Contact)
      const objectType = recordType || 'Lead';
      return `${stats.value.instance_url}/lightning/r/${objectType}/${recordId}/view`;
    };

    // Watch for tab changes to load data on demand
    watch(activeTab, (newTab, oldTab) => {
      if (newTab !== oldTab) {
        if (newTab === 'logs' && logs.value.length === 0) {
          loadLogs();
        } else if (newTab === 'queue' && queueItems.value.length === 0) {
          loadQueue();
        } else if (newTab === 'pending' && pendingCampaigns.value.length === 0) {
          loadPendingCampaigns();
        }
      }
    });

    // Lifecycle
    onMounted(() => {
      // Load stats immediately (fast)
      loadStats();
      loadHealthStatus();
      loadConfigurations();
      
      // Load tab data only when needed
      if (activeTab.value === 'logs') {
        loadLogs();
      } else if (activeTab.value === 'queue') {
        loadQueue();
      } else if (activeTab.value === 'pending') {
        loadPendingCampaigns();
      }
      
      // Auto-refresh stats every 30 seconds
      refreshInterval = setInterval(() => {
        loadStats();
        loadHealthStatus();
      }, 30000);
    });

    onUnmounted(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    });

    return {
      isLoading,
      logsLoading,
      statsLoading,
      queueLoading,
      pendingLoading,
      timeRange,
      activeTab,
      stats,
      healthStatus,
      healthStatusClass,
      logs,
      logsPagination,
      logFilter,
      queueItems,
      queuePagination,
      pendingCampaigns,
      pendingPagination,
      configurations,
      selectedCampaigns,
      selectAll,
      bulkSyncConfig,
      showLogModal,
      selectedLog,
      showBulkSyncModal,
      tabs,
      refreshData,
      loadLogs,
      loadQueue,
      loadPendingCampaigns,
      viewLogDetail,
      retrySync,
      deleteLog,
      clearAllErrors,
      retryQueueItem,
      retryFailedSync,
      syncSingleCampaign,
      toggleAllCampaigns,
      executeBulkSync,
      getStatusClass,
      formatDate,
      getSalesforceUrl
    };
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>